/**
 * Static lookup tables used by list-filter chips, form selects, and
 * timeline / badge rendering. Phase 5 で `field_definitions` テーブルを
 * 削除した結果、 旧実装が DB メタデータで保持していた選択肢はここに
 * 集約する。 値・ ラベルは旧
 * `src/lib/field-definitions/choice-derivation.ts` / `src/lib/utils/labels.ts`
 * からの転記を原則とし、 業務側が画面で違和感を覚えないよう原文を保つ。
 */

import { OPPORTUNITY_STATUS_LABELS, type OpportunityStatus } from '@shared/types/opportunity';
import { CONTRACT_STATUS_LABELS, type ContractStatus } from '@shared/types/contract';
import {
  SALES_STATUS_LABELS,
  SALES_STATUS_ORDER,
  ACTION_STATUS_LABELS,
  ACTION_TYPE_LABELS,
  ACTION_TYPE_ORDER,
  COMPLETION_RESULT_LABELS,
  COMPLETION_RESULT_ORDER,
  type SalesStatus,
  type ActionStatus,
  type ActionType,
  type CompletionResult,
} from '@shared/types/sales-action';
import {
  CUSTOMER_PROBLEM_STAGE_LABELS,
  CUSTOMER_PROBLEM_STAGE_ORDER,
  type CustomerProblemStage,
} from '@shared/types/customer-problem';
import { ADOPTION_STATUS_LABELS, type AdoptionStatus } from '@shared/types/recommendation';
import {
  SALES_MATERIAL_TYPE_LABELS,
  type SalesMaterialType,
} from '@shared/types/product-sales-material';
import type { DialogueLogType } from '@shared/types/dialogue-log';
import { ADDRESS_PREFECTURE_OPTIONS } from './address-master';

export interface SimpleOption {
  value: string;
  label: string;
}

function optionsFromLabels<T extends string>(
  labels: Record<T, string>,
  order: readonly T[],
): SimpleOption[] {
  return order.map((value) => ({ value, label: labels[value] }));
}

// ============================================================================
// 1. 地域 (prefecture / city)
// ============================================================================

export const PREFECTURE_OPTIONS: SimpleOption[] = [...ADDRESS_PREFECTURE_OPTIONS];

// ============================================================================
// 2. 弁護士会
// ============================================================================

/**
 * 画面項目カタログ「選択肢マスタ」の弁護士会マスタ (全国52会・正式名称) に整合。
 * lawyers/firms.bar_association のデータも正式名称 (例: 東京弁護士会) を保持する。
 */
export const BAR_ASSOCIATION_OPTIONS: SimpleOption[] = [
  '東京弁護士会',
  '第一東京弁護士会',
  '第二東京弁護士会',
  '神奈川県弁護士会',
  '埼玉弁護士会',
  '千葉県弁護士会',
  '茨城県弁護士会',
  '栃木県弁護士会',
  '群馬弁護士会',
  '静岡県弁護士会',
  '山梨県弁護士会',
  '長野県弁護士会',
  '新潟県弁護士会',
  '大阪弁護士会',
  '京都弁護士会',
  '兵庫県弁護士会',
  '奈良弁護士会',
  '滋賀弁護士会',
  '和歌山弁護士会',
  '愛知県弁護士会',
  '三重弁護士会',
  '岐阜県弁護士会',
  '福井弁護士会',
  '金沢弁護士会',
  '富山県弁護士会',
  '広島弁護士会',
  '山口県弁護士会',
  '岡山弁護士会',
  '鳥取県弁護士会',
  '島根県弁護士会',
  '福岡県弁護士会',
  '佐賀県弁護士会',
  '長崎県弁護士会',
  '大分県弁護士会',
  '熊本県弁護士会',
  '鹿児島県弁護士会',
  '宮崎県弁護士会',
  '沖縄弁護士会',
  '仙台弁護士会',
  '福島県弁護士会',
  '山形県弁護士会',
  '岩手弁護士会',
  '秋田弁護士会',
  '青森県弁護士会',
  '札幌弁護士会',
  '函館弁護士会',
  '旭川弁護士会',
  '釧路弁護士会',
  '香川県弁護士会',
  '徳島弁護士会',
  '高知弁護士会',
  '愛媛弁護士会',
].map((v) => ({ value: v, label: v }));

/** 弁護士会の正式名称から画面表示用ラベルを作る。 保存値は正式名称のまま維持する。 */
export function formatBarAssociationLabel(value: string | null | undefined): string {
  if (!value) return '-';
  return value.replace(/弁護士会$/, '');
}

export const BAR_ASSOCIATION_DISPLAY_OPTIONS: SimpleOption[] = BAR_ASSOCIATION_OPTIONS.map(
  (option) => ({
    value: option.value,
    label: formatBarAssociationLabel(option.label),
  }),
);

// 役職 (lawyers.position) の選択肢は静的定数を廃止し、 役職マスタ
// (`GET /api/lawyer-positions`, `@shared/api/lawyer-positions`) から動的取得する
// (catalog No.49 / 55 / 69 / 87 「役職マスタ参照」)。 lawyers.position は DB 側で
// FK 制約され、 候補は master が single source。

// ============================================================================
// 3. 顧客ステータス (sales_actions.sales_status)
// ============================================================================

/**
 * 一覧の「ステータス」と営業アクションの
 * 顧客ステータス select で使う9値。
 */
export const CUSTOMER_STATUS_OPTIONS: SimpleOption[] = [
  { value: 'no_contact', label: '未接点' },
  { value: 'contacted', label: '接点あり' },
  { value: 'in_dialogue', label: '対話あり' },
  { value: 'appointment', label: 'アポ予定' },
  { value: 'negotiation_c', label: '商談/C' },
  { value: 'negotiation_b', label: '商談/B' },
  { value: 'negotiation_a', label: '商談/A' },
  { value: 'won', label: '受注' },
  { value: 'lost', label: '失注' },
];

export const CUSTOMER_STATUS_LABELS: Record<string, string> = Object.fromEntries(
  CUSTOMER_STATUS_OPTIONS.map((o) => [o.value, o.label]),
);

/**
 * 旧名互換 alias。 中身は sales_actions.sales_status の9値。
 */
export const OPPORTUNITY_STATUS_OPTIONS = CUSTOMER_STATUS_OPTIONS;

/**
 * ステータスフィルタの値集合 = 顧客ステータス9値 (受注/失注を含む)。
 * 一覧の既定はステータス絞り込みなし (全9値表示)。受注/失注 top の事務所・弁護士も
 * 一覧から到達できる (除外していた旧仕様は、受注/失注の相手を一覧で見る手段が
 * なくなるため 2026-07-22 に撤回)。
 */
export const CUSTOMER_STATUS_VALUES: string[] = CUSTOMER_STATUS_OPTIONS.map((o) => o.value);

/**
 * ステータスフィルタが既定 (= 未指定 or 全9値) かどうか。 既定はフィルタ
 * 未指定として扱う (一覧のチップ・詳細条件ドロワーで「絞り込みなし」表示にする)。
 */
export function isDefaultCustomerStatuses(values: string[] | undefined): boolean {
  if (values === undefined) return true;
  if (values.length !== CUSTOMER_STATUS_VALUES.length) return false;
  return CUSTOMER_STATUS_VALUES.every((value) => values.includes(value));
}

// ============================================================================
// 4. ステータス代表値 (9 値 CustomerStatusDisplay)
// ============================================================================

export const SALES_PROPOSAL_DISPLAY_STATUS_LABEL: Record<string, string> = {
  no_contact: '未接点',
  contacted: '接点あり',
  in_dialogue: '対話あり',
  appointment: 'アポ予定',
  negotiation_c: '商談/C',
  negotiation_b: '商談/B',
  negotiation_a: '商談/A',
  won: '受注',
  lost: '失注',
};

export const SALES_PROPOSAL_DISPLAY_STATUS_BADGE: Record<string, string> = {
  no_contact: 'badge-gray-pale',
  contacted: 'badge-blue-pale',
  in_dialogue: 'badge-blue-pale',
  appointment: 'badge-orange',
  negotiation_c: 'badge-orange',
  negotiation_b: 'badge-green-pale',
  negotiation_a: 'badge-green-pale',
  won: 'badge-green',
  lost: 'badge-red',
};

// ============================================================================
// 5. 失注理由 (sales_actions.lost_reason)
// ============================================================================

export const LOST_REASON_OPTIONS: SimpleOption[] = [
  { value: 'budget_not_secured', label: '予算未確保' },
  { value: 'competitor_adopted', label: '競合採用' },
  { value: 'too_early', label: '時期尚早' },
  { value: 'needs_mismatch', label: 'ニーズ不一致' },
  { value: 'internal_alignment_failed', label: '社内調整不調' },
  { value: 'no_response', label: '反応途絶' },
  { value: 'other', label: 'その他' },
];

// ============================================================================
// 6. 一覧フィルタの期間バケット / 件数バケット
// ============================================================================

/** 所属弁護士数バケット (catalog No.33)。 法律事務所一覧の列フィルタと詳細条件で共用。 */
export const LAWYER_COUNT_BUCKET_OPTIONS: SimpleOption[] = [
  { value: 'one', label: '1人' },
  { value: 'two', label: '2人' },
  { value: 'three_to_five', label: '3〜5人' },
  { value: 'six_to_ten', label: '6〜10人' },
  { value: 'eleven_to_twenty', label: '11〜20人' },
  { value: 'twenty_one_to_thirty', label: '21〜30人' },
  { value: 'thirty_one_to_fifty', label: '31〜50人' },
  { value: 'fifty_one_to_hundred', label: '51〜100人' },
  { value: 'one_hundred_one_plus', label: '101人以上' },
];

export const LAST_CONTACT_BUCKET_OPTIONS: SimpleOption[] = [
  { value: '7d', label: '7 日以内' },
  { value: '30d', label: '30 日以内' },
  { value: '90d', label: '90 日以内' },
  { value: 'over_90d', label: '90 日超' },
  { value: 'never', label: '未接触' },
];

export const NEXT_ACTION_BUCKET_OPTIONS: SimpleOption[] = [
  { value: 'overdue', label: '期限超過' },
  { value: 'today', label: '今日' },
  { value: 'this_week', label: '今週中' },
  { value: 'not_set', label: '未設定' },
];

// ============================================================================
// 7. 組織形態 / 事務所種別 (firms.organization_type, firms.firm_type)
// ============================================================================

/** 画面項目カタログ「選択肢マスタ」の組織形態マスタ(3値)に整合。 */
export const ORGANIZATION_TYPE_OPTIONS: SimpleOption[] = [
  '弁護士法人',
  '個人事務所',
  '共同事務所',
].map((v) => ({ value: v, label: v }));

// ============================================================================
// 7. レベル系 (it_literacy / individual_it_literacy / decision_influence)
//    low / mid / high → 低 / 中 / 高
// ============================================================================

export const LEVEL_LMH_OPTIONS: SimpleOption[] = [
  { value: 'high', label: '高' },
  { value: 'mid', label: '中' },
  { value: 'low', label: '低' },
];

export const LEVEL_LMH_LABELS: Record<string, string> = {
  high: '高',
  mid: '中',
  low: '低',
};

export const CLIENT_RATIO_CATEGORY_OPTIONS: SimpleOption[] = [
  '個人中心',
  '法人中心',
  '個人:法人=50:50',
].map((value) => ({ value, label: value }));

/**
 * `lawyers.approach_priority` 専用。 旧データは high / medium / low を使う
 * 一方、 新実装は `mid` も互換的に許容する。 ラベルは 3 値とも 高 / 中 / 低。
 */
export const APPROACH_PRIORITY_OPTIONS: SimpleOption[] = [
  { value: 'high', label: '高' },
  { value: 'medium', label: '中' },
  { value: 'low', label: '低' },
];

export const APPROACH_PRIORITY_LABELS: Record<string, string> = {
  high: '高',
  medium: '中',
  mid: '中',
  low: '低',
};

// ============================================================================
// 9. 主要取扱分野 (firms.practice_areas / lawyers.practice_areas)
// ============================================================================
//
// catalog-alignment Phase 13 で firms から撤去した列の選択肢定数
// (CLIENT_INDUSTRY_OPTIONS / ESTIMATED_REVENUE_RANGE_OPTIONS / IT_TOOL_OPTIONS /
// CASE_MANAGEMENT_TOOL_OPTIONS) は参照元が消えたため削除した。

// 取扱分野マスタの基底 12 値。firms と lawyers で選択肢配列を共有しない
// (spec 20260716 Scope C: 「公益活動」は弁護士のみ。DB CHECK も別管理)。
const BASE_PRACTICE_AREAS = [
  '企業法務',
  '離婚・男女問題',
  '相続・遺言',
  '交通事故',
  '債務整理',
  '借地借家',
  '風評対策',
  '訴訟',
  '刑事',
  '不動産',
  '倒産・事業再生',
  '消費者問題',
];

/** 法律事務所の主要取扱分野 (firms.practice_areas)。取扱分野マスタ 12 値に整合。 */
export const FIRM_PRACTICE_AREA_OPTIONS: SimpleOption[] = BASE_PRACTICE_AREAS.map(
  (v) => ({ value: v, label: v }),
);

/** 弁護士の専門分野 (lawyers.practice_areas)。基底 12 値 + 「公益活動」の 13 値。 */
export const LAWYER_PRACTICE_AREA_OPTIONS: SimpleOption[] = [
  ...BASE_PRACTICE_AREAS,
  '公益活動',
].map((v) => ({ value: v, label: v }));

// ============================================================================
// 10. 担当訴訟案件数 (lawyers.litigation_case_load)
// ============================================================================

/** spec 20260716 Scope B の 5 区分。未設定 (NULL) は選択解除で表現する。 */
export const LITIGATION_CASE_LOAD_OPTIONS: SimpleOption[] = [
  { value: 'one_to_five', label: '1〜5件程度' },
  { value: 'six_to_ten', label: '6〜10件程度' },
  { value: 'eleven_to_twenty', label: '11〜20件程度' },
  { value: 'twenty_one_or_more', label: '21件以上' },
  { value: 'does_not_handle_litigation', label: '訴訟案件は対応していない' },
];

// ============================================================================
// 11. コミュニティ参加 (lawyers.community_types)
// ============================================================================

/** 画面項目カタログ「選択肢マスタ」のコミュニティマスタ (連絡経路) 3 値に整合。 */
export const COMMUNITY_TYPE_OPTIONS: SimpleOption[] = [
  'Facebook',
  'メール',
  'LINE',
].map((v) => ({ value: v, label: v }));

// ============================================================================
// 12. 対話チャネル (dialog_logs.channel) と所感 (dialog_logs.sentiment)
// ============================================================================

/**
 * 旧の Modal / Timeline 双方を統合した 7 値。 タイムラインバッジ表記との
 * 整合は `widgets/util/timeline-builder.ts` 側で同じマップを使う。
 *
 * - meeting: 旧 choice-derivation では「名刺交換」 だったが、 タイムライン
 *   builder は「対面/Zoom 商談」 とラベリングしている。 業務確認後 (§12)
 *   どちらかに統一する。
 */
export const DIALOG_CHANNEL_OPTIONS: SimpleOption[] = [
  { value: 'meeting', label: '対面/Zoom 商談' },
  { value: 'call', label: '電話' },
  { value: 'phone', label: '電話' },
  { value: 'visit', label: '訪問' },
  { value: 'email', label: '個別メール' },
  { value: 'chat', label: 'チャット' },
  { value: 'zoom', label: 'Zoom' },
  { value: 'other', label: 'その他' },
];

export const DIALOG_CHANNEL_LABELS: Record<string, string> = Object.fromEntries(
  DIALOG_CHANNEL_OPTIONS.map((o) => [o.value, o.label]),
);

/**
 * dialogue_logs.type の表示メタ (`timeline.md` §3)。 `email` は手動追加
 * モーダルでも選択でき、 メール本文をそのまま貼り付けて登録できる。
 *
 * 順序とラベルは追加モーダルの「対話方法」ボタン並び
 * (20260613-dialogue-log-method-buttons 仕様)。 icon はモーダルでは使わず、
 * タイムラインカードのバッジ表示でのみ使う。
 */
export interface DialogueLogTypeOption {
  value: DialogueLogType;
  label: string;
  icon: string;
}

export const DIALOGUE_LOG_TYPE_OPTIONS: DialogueLogTypeOption[] = [
  { value: 'phone', label: '電話', icon: '📞' },
  { value: 'email', label: 'メール', icon: '✉️' },
  { value: 'in_person', label: '面談', icon: '🏢' },
  { value: 'online_mtg', label: 'オンライン', icon: '💻' },
  { value: 'other', label: 'その他', icon: '🗒️' },
];

export const DIALOGUE_LOG_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  DIALOGUE_LOG_TYPE_OPTIONS.map((o) => [o.value, o.label]),
);

export const DIALOGUE_LOG_TYPE_ICONS: Record<string, string> = Object.fromEntries(
  DIALOGUE_LOG_TYPE_OPTIONS.map((o) => [o.value, o.icon]),
);

/**
 * 営業イベント (商談作成 / ステータス変更 / 契約締結) 共通の表示メタ。
 * 内部カテゴリ名は `sales_event` のまま。 ユーザー向け表示ラベルのみ「商談/契約」。
 */
export const SALES_EVENT_ICON = '⚙️';
export const SALES_EVENT_LABEL = '商談/契約';

/** dialog_logs.sentiment 3 値。 旧 choice-derivation のモーダル表記。 */
export const SENTIMENT_OPTIONS: SimpleOption[] = [
  { value: 'positive', label: 'ポジティブ' },
  { value: 'neutral', label: '中立' },
  { value: 'negative', label: 'ネガティブ' },
];

export const SENTIMENT_LABELS: Record<string, string> = Object.fromEntries(
  SENTIMENT_OPTIONS.map((o) => [o.value, o.label]),
);

/** タイムラインバッジ用の sentiment 短縮ラベル。 旧 UI の表記。 */
export const SENTIMENT_BADGE_LABELS: Record<string, string> = {
  positive: '好印象',
  neutral: '中立',
  negative: '懸念あり',
};

// ============================================================================
// 13. 顧客課題ステージ / 営業アクション系 (派生)
// ============================================================================

export const CUSTOMER_PROBLEM_STAGE_OPTIONS: SimpleOption[] = optionsFromLabels<CustomerProblemStage>(
  CUSTOMER_PROBLEM_STAGE_LABELS,
  CUSTOMER_PROBLEM_STAGE_ORDER,
);

export const SALES_STATUS_OPTIONS: SimpleOption[] = optionsFromLabels<SalesStatus>(
  SALES_STATUS_LABELS,
  SALES_STATUS_ORDER,
);

export const ACTION_STATUS_OPTIONS: SimpleOption[] = optionsFromLabels<ActionStatus>(
  ACTION_STATUS_LABELS,
  ['open', 'completed', 'canceled'],
);

export const ACTION_TYPE_OPTIONS: SimpleOption[] = optionsFromLabels<ActionType>(
  ACTION_TYPE_LABELS,
  ACTION_TYPE_ORDER,
);

export const COMPLETION_RESULT_OPTIONS: SimpleOption[] = optionsFromLabels<CompletionResult>(
  COMPLETION_RESULT_LABELS,
  COMPLETION_RESULT_ORDER,
);

// ============================================================================
// 14. 商談・ 契約 ステータス (派生)
// ============================================================================

export const OPPORTUNITY_DETAIL_STATUS_OPTIONS: SimpleOption[] = optionsFromLabels<OpportunityStatus>(
  OPPORTUNITY_STATUS_LABELS,
  ['prospect', 'qualified', 'proposal', 'negotiation', 'won', 'lost'],
);

export const CONTRACT_STATUS_OPTIONS: SimpleOption[] = optionsFromLabels<ContractStatus>(
  CONTRACT_STATUS_LABELS,
  ['draft', 'active', 'canceled', 'expired'],
);

/**
 * 法律事務所一覧の契約フィルタ候補。 契約ステータス 4 値の前に `uncontracted`
 * (active 契約なし) を足したもの。 詳細条件ドロワー (`FirmAdvancedFilterDrawer`) と
 * firm 一覧条件の validator (`firm-list-view-state`) が同じ供給元を参照するための単一ソース。
 */
export const CONTRACT_FILTER_OPTIONS: SimpleOption[] = [
  { value: 'uncontracted', label: '未契約' },
  ...CONTRACT_STATUS_OPTIONS,
];

// ============================================================================
// 15. AI レコメンド / 営業資料 (派生)
// ============================================================================

export const ADOPTION_STATUS_OPTIONS: SimpleOption[] = optionsFromLabels<AdoptionStatus>(
  ADOPTION_STATUS_LABELS,
  ['pending', 'accepted', 'rejected', 'superseded'],
);

export const SALES_MATERIAL_TYPE_OPTIONS: SimpleOption[] = optionsFromLabels<SalesMaterialType>(
  SALES_MATERIAL_TYPE_LABELS,
  ['service_deck', 'proposal_deck', 'pricing_sheet', 'case_study', 'contract_related', 'other'],
);

// ============================================================================
// 16. 弁護士マスタ (修習地 / 出身大学 / 出身ロースクール / 副業意向 3 種)
//     画面項目カタログ「選択肢マスタ」(2026-05-25) より転記。 value = label。
//     既存データに無い値でも InlineField は raw 表示で許容する (displayFor)。
// ============================================================================

/** 修習地マスタ (catalog 修習地, 全 51 箇所)。 */
export const TRAINING_LOCATION_OPTIONS: SimpleOption[] = [
  '東京','立川','横浜','さいたま','千葉','水戸','宇都宮','前橋','静岡','甲府',
  '長野','新潟','大阪','京都','神戸','奈良','大津','和歌山','名古屋','津',
  '岐阜','福井','金沢','富山','広島','山口','岡山','鳥取','松江','福岡',
  '佐賀','長崎','大分','熊本','鹿児島','宮崎','那覇','仙台','福島','山形',
  '盛岡','秋田','青森','札幌','函館','旭川','釧路','高松','徳島','高知',
  '松山',
].map((v) => ({ value: v, label: v }));

/** 出身大学マスタ (catalog 大学, 主要法学部設置校 99 校)。 */
export const UNIVERSITY_OPTIONS: SimpleOption[] = [
  '東京大学','京都大学','大阪大学','東北大学','九州大学','名古屋大学',
  '北海道大学','一橋大学','神戸大学','千葉大学','横浜国立大学','筑波大学',
  '金沢大学','岡山大学','広島大学','新潟大学','信州大学','静岡大学',
  '熊本大学','島根大学','鹿児島大学','香川大学','愛媛大学','琉球大学',
  '山形大学','三重大学','山口大学','岩手大学','大分大学','宮崎大学',
  '東京都立大学','大阪公立大学','横浜市立大学','名古屋市立大学','北九州市立大学','高崎経済大学',
  '慶應義塾大学','早稲田大学','中央大学','明治大学','立教大学','青山学院大学',
  '法政大学','上智大学','学習院大学','東洋大学','日本大学','専修大学',
  '駒澤大学','東海大学','成蹊大学','成城大学','明治学院大学','獨協大学',
  '国学院大学','国士舘大学','創価大学','亜細亜大学','大東文化大学','拓殖大学',
  '帝京大学','東京経済大学','二松学舎大学','桜美林大学','神奈川大学','関東学院大学',
  '桐蔭横浜大学','白鷗大学','駿河台大学','東洋英和女学院大学','中京大学','城西大学',
  '麗澤大学','西武文理大学','同志社大学','立命館大学','関西大学','関西学院大学',
  '京都産業大学','龍谷大学','近畿大学','甲南大学','神戸学院大学','大阪学院大学',
  '大阪経済法科大学','桃山学院大学','南山大学','愛知大学','名城大学','愛知学院大学',
  '金沢工業大学','福岡大学','西南学院大学','久留米大学','東北学院大学','北海学園大学',
  '広島修道大学','松山大学','沖縄大学',
].map((v) => ({ value: v, label: v }));

/** 出身ロースクールマスタ (catalog ロースクール, 現存 + 廃校 73 校)。 */
export const LAW_SCHOOL_OPTIONS: SimpleOption[] = [
  '北海道大学','東北大学','筑波大学','千葉大学','東京大学','一橋大学',
  '金沢大学','名古屋大学','京都大学','大阪大学','神戸大学','岡山大学',
  '広島大学','九州大学','琉球大学','東京都立大学','大阪公立大学','学習院大学',
  '慶應義塾大学','上智大学','専修大学','創価大学','中央大学','日本大学',
  '法政大学','明治大学','早稲田大学','愛知大学','南山大学','同志社大学',
  '立命館大学','関西大学','関西学院大学','福岡大学','姫路獨協大学','大宮法科大学院大学',
  '駿河台大学','明治学院大学','神戸学院大学','東北学院大学','大阪学院大学','島根大学',
  '東海大学','大東文化大学','獨協大学','白鷗大学','信州大学','関東学院大学',
  '新潟大学','龍谷大学','久留米大学','鹿児島大学','香川大学・愛媛大学 (2大学連合)','広島修道大学',
  '静岡大学','熊本大学','東洋大学','愛知学院大学','京都産業大学','山梨学院大学',
  '神奈川大学','國學院大学','中京大学','成蹊大学','名城大学','北海学園大学',
  '立教大学','桐蔭横浜大学','青山学院大学','横浜国立大学','近畿大学','西南学院大学',
  '甲南大学',
].map((v) => ({ value: v, label: v }));

/** 副業意向マスタ (catalog No.104)。 */
export const SIDE_WORK_INTENTION_OPTIONS: SimpleOption[] = [
  'やりたい','やりたくない','やりたいが時間がない',
].map((v) => ({ value: v, label: v }));

/** 副業時間意向マスタ (catalog No.105)。 */
export const SIDE_WORK_TIME_INTENTION_OPTIONS: SimpleOption[] = [
  '週1','週2','週3','週4','週5',
].map((v) => ({ value: v, label: v }));

/**
 * 副業報酬意向マスタ (catalog No.106)。 カタログ原本の 2 値目は誤記
 * (90001-12000) のため、 業務確認 (2026-05-28) で 8001-12000 に是正して転記。
 */
export const SIDE_WORK_COMPENSATION_INTENTION_OPTIONS: SimpleOption[] = [
  '5000-8000','8001-12000','12001-20000','20000-',
].map((v) => ({ value: v, label: v }));

// ============================================================================
// 17. 懲戒情報 (lawyers.disciplinary_*)
// ============================================================================

export const DISCIPLINARY_CHECK_STATUS_OPTIONS: SimpleOption[] = [
  { value: 'unchecked', label: '未確認' },
  { value: 'none', label: 'なし' },
  { value: 'found', label: 'あり' },
];

export const DISCIPLINARY_SALES_HANDLING_OPTIONS: SimpleOption[] = [
  { value: 'normal', label: '通常' },
  { value: 'needs_review', label: '要確認' },
  { value: 'stop_contact', label: '接触停止' },
];

// ============================================================================
// 18. 修習期 (lawyers.legal_training_term)
//     登録番号レンジから自動算出される修習期。 value = label。
//     60期から64期は現行/新を分け、65期以降は通常表記にする。
// ============================================================================

export const TRAINING_TERM_OPTIONS: SimpleOption[] = [
  '78期','77期','76期','75期','74期','73期','72期','71期','70期','69期',
  '68期','67期','66期','65期','新64期','新63期','新62期','新61期','新60期','現行64期',
  '現行63期','現行62期','現行61期','現行60期','59期','58期','57期','56期','55期','54期',
  '53期','52期','51期','50期','49期','48期','47期','46期','45期','44期',
  '43期','42期','41期','40期','39期','38期','37期','36期','35期','34期',
  '33期','32期','31期','30期','29期','28期','27期','26期','25期','24期',
  '23期','22期','21期','20期','19期','18期','17期','16期','15期','14期',
  '13期','12期','11期','10期','9期','8期','7期','6期','5期','4期',
  '3期','2期','1期',
].map((v) => ({ value: v, label: v }));

/**
 * 弁護士一覧の修習期フィルタ候補。 修習期マスタ (TRAINING_TERM_OPTIONS) の末尾に
 * 「未設定」 (`__unset__` 擬似値) を足したもの。 LawyerTable の列フィルタと lawyer
 * 一覧条件の validator (`lawyer-list-view-state`) が同じ供給元を参照するための単一ソース。
 */
export const LEGAL_TRAINING_TERM_FILTER_OPTIONS: SimpleOption[] = [
  ...TRAINING_TERM_OPTIONS,
  { value: '__unset__', label: '未設定' },
];

// ============================================================================
// 19. 主要SNS (lawyers.major_sns)
//     catalog No.97。 value = label。
// ============================================================================

export const MAJOR_SNS_OPTIONS: SimpleOption[] = [
  'Facebook',
  'X',
  'LINE',
  'LinkedIn',
].map((v) => ({ value: v, label: v }));

export const VERIFICATION_STATUS_OPTIONS: SimpleOption[] = [
  { value: 'unconfirmed', label: '未確認' },
  { value: 'confirmed', label: '確認済' },
  { value: 'denied', label: '否定' },
];
